<template>
    <div>
        <div
            class="tooltip-header"
        >
            <div class="spaced-flex-row q-mb-xs text-h6">
                <span data-cy="streamflow-title">{{ props.chartData.isVirtual ? '' : 'Gauge: ' }}{{ props.chartData.gaugeName }}</span>
                <q-btn
                    v-if="props.chartType === 'tooltip'"
                    icon="close"
                    dense
                    flat
                    data-cy="streamflow-close-button"
                    @click="emit('close')"
                />
            </div>
            <VirtualGaugeInfoButton
                v-if="props.chartData.isVirtual"
            />
            <div
                v-if="props.chartType !== 'report' && props.chartType !== 'log'"
                class="q-mb-sm"
            >
                <span v-if="props.chartData.mostRecentFlowTimestamp">
                    Last Update: {{ formatDate(locationUpdateTime).replace('|', `${props.chartData.gaugeTzAbbr} |`) }}
                    <span
                        v-if="props.chartData.gaugeTzAbbr !== settingStore.userTzAbbr"
                        class="q-mb-none"
                    >
                        ({{ userUpdateTime.getHours() % 12 }}:{{ userUpdateTime.getMinutes() === 0 ? '00' : userUpdateTime.getMinutes() }} {{ userUpdateTime.getHours() > 11 ? 'PM' : 'AM' }}
                        {{ settingStore.userTzAbbr }}<span v-if="locationUpdateTime.getDay() > userUpdateTime.getDay()">&nbsp;Yesterday</span><span v-if="locationUpdateTime.getDay() < userUpdateTime.getDay()">&nbsp;Tomorrow</span>
                        <span v-if="locationUpdateTime.getDay() < userUpdateTime.getDay()">&nbsp;Yesterday</span>)
                    </span>
                </span>
                <span v-else>
                    Last Update: Not Available
                </span>
            </div>
        </div>
        <GaugeMetadata
            v-if="props.chartType !== 'log'"
            :gauge-data="props.chartData"
            :is-report="props.chartType === 'report'"
        />
        <div
            v-if="props.chartType !== 'tooltip' && props.chartType !== 'log' && (props.chartData.hasHistoricalData || props.chartData.hasRecentData || props.chartData.isVirtual)"
            data-cy="latest-flow-table"
        >
            <table
                class="left-grey-table"
            >
                <tbody>
                    <tr
                        v-for="data in tableContent"
                        :key="data"
                    >
                        <td class="grey-lighter-background">
                            {{ data.header }}
                            <p
                                v-if="props.chartData.isVirtual && data.id === 'short'"
                                class="vg-time"
                            >
                                {{ formatDate(locationUpdateTime, false, false, true, false).replace('| ', '') }} - {{ formatDate(addDaysToDate(locationUpdateTime, 1), false, false, true, false).replace('| ', '') }} {{ props.chartData.gaugeTzAbbr }}
                            </p>
                        </td>
                        <td>
                            <div
                                class="flex"
                                :data-cy="`${data.id}-value`"
                            >
                                <RiskIcon
                                    v-if="data.id === 'long' ? longRiskLevel : shortRiskLevel"
                                    class="q-mr-sm"
                                    :risk-level="getSeverity('gaugeLevels', data.id === 'long' ? longRiskLevel : shortRiskLevel)?.riskLevel"
                                    :risk-color="getSeverity('gaugeLevels', data.id === 'long' ? longRiskLevel : shortRiskLevel)?.color"
                                    :icon-shape="props.chartData.isVirtual ? 'rhombus' : 'square'"
                                    :is-report="props.chartType==='report'"
                                />
                                <div>
                                    {{ data.value }}
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div
            v-if="props.chartData.hasHistoricalData || props.chartData.hasRecentData || props.chartData.hasReportData || props.chartData.isVirtual"
            class="header-container q-ma-sm"
        >
            <div class="text-h6 q-mt-sm q-mb-md">
                {{ viewType.text }} ({{ convertUnit('m³/s') }})
            </div>
            <div class="chart-options-container">
                <q-select
                    v-if="viewTypeOptions.length > 1"
                    v-model="viewType"
                    :options="viewTypeOptions"
                    outlined
                    dense
                    class="range-select"
                />
                <q-btn
                    v-if="props.chartData.hasReturnPeriods || chartOptions?.riskThresholds?.length > 0"
                    class="layers-button"
                    label="Chart Layers"
                    no-caps
                    outline
                >
                    <q-menu class="q-pa-md">
                        <div>
                            <div class="text-bold q-ml-sm">
                                Risk Thresholds
                                <q-badge
                                    v-if="props.chartType !== 'tooltip' && !props.chartData.defaultRiskLevels"
                                    color="green-5"
                                    text-color="black"
                                >
                                    Custom Thresholds
                                </q-badge>
                            </div>
                            <div
                                v-if="chartOptions?.riskThresholds?.length > 0"
                                class="risk-thresholds"
                            >
                                <q-checkbox
                                    v-model="chartOptions.showAllRiskLevels"
                                    label="Show all Risk Levels"
                                    data-cy="all-risk-threshold-checkbox"
                                    @update:model-value="toggleAllRiskLevels()"
                                />
                                <div
                                    v-for="rp in chartOptions.riskThresholds"
                                    :key="rp.riskLevel"
                                    class="q-ml-sm"
                                >
                                    <q-checkbox
                                        v-model="rp.showRiskThreshold"
                                        :label="getRiskLevelText(rp.riskLevel, props.chartData.defaultRiskLevels, true)"
                                        :class="`level-${rp.color.replace('#', '')}`"
                                        :data-cy="`risk-threshold-checkbox-${rp.riskLevel}`"
                                    />
                                </div>
                            </div>
                            <p
                                v-else
                                class="q-ml-sm q-mb-none"
                            >
                                Risk Thresholds have not been set up for this gauge
                            </p>
                            <q-separator
                                class="q-ma-sm"
                            />
                        </div>
                        <div
                            class="return-periods"
                        >
                            <div class="text-bold q-ml-sm">
                                Return Periods
                            </div>
                            <q-checkbox
                                v-if="chartOptions.returnPeriods.length > 0"
                                v-model="chartOptions.showReturnPeriods"
                                label="Show all available Return Periods"
                                data-cy="return-period-checkbox"
                            />
                            <p
                                v-else
                                class="q-ml-sm q-mb-none"
                            >
                                Return Periods cannot be generated due to insufficient data
                            </p>
                        </div>
                    </q-menu>
                </q-btn>
                <q-btn
                    v-if="enableLayerReset"
                    label="Reset"
                    class="reset-button q-ml-sm"
                    no-caps
                    flat
                    dense
                    @click="resetChartLayers()"
                />
            </div>
        </div>
        <div
            v-if="(props.chartData.hasHistoricalData || props.chartData.hasRecentData || props.chartData.hasReportData) && (viewType.value !== 'lastSevenDayFlowTimeSeries' && viewType.value !== 'lastDayFlowTimeSeries')"
            class="risk-thresholds"
        >
            <q-checkbox
                v-model="chartOptions.showFlow"
                color="black"
                label="Daily Average Streamflow"
            />
            <q-checkbox
                v-model="chartOptions.showPeak"
                color="primary"
                label="Daily Peak Streamflow"
            />
        </div>
        <!-- Chart Containers -->
        <div
            v-if="props.chartData.hasHistoricalData || props.chartData.hasRecentData || props.chartData.hasReportData || props.chartData.isVirtual"
            id="streamflow-chart-container"
        >
            <div
                class="svg-wrap"
                :class="`${props.chartType}-mode`"
            >
                <svg
                    class="svg-webkit-fix"
                    :class="`streamflow-chart-${props.chartType}`"
                >
                    <!-- Streamflow Chart -->
                </svg>
            </div>
        </div>
        <div
            v-if="props.chartData.hasHistoricalData || props.chartData.hasRecentData || props.chartData.hasReportData || props.chartData.isVirtual"
            id="streamflow-timeline-container"
        >
            <svg
                class="svg-webkit-fix"
                :class="`streamflow-timeline-${props.chartType}`"
            >
                <!-- Timeline Chart -->
            </svg>
        </div>
        <div
            v-else
            class="no-hydrograph-data"
        >
            There is no hydrograph for the selected time frame
        </div>
        <!-- Chart Legend -->
        <div
            class="sfc-legend q-mb-sm"
        >
            <div
                v-for="rp in chartOptions.riskThresholds.filter(rp => rp.showRiskThreshold)"
                :key="rp.riskLevel"
                class="sfc-legend-item q-my-xs"
            >
                <RiskIcon
                    :risk-level="rp.riskLevel"
                    :risk-color="rp.color"
                    :icon-shape="props.chartData.isVirtual ? 'rhombus' : 'square'"
                    :is-report="props.chartType==='report'"
                />
                <span class="q-ml-sm">{{ getRiskLevelText(rp.riskLevel, props.chartData.defaultRiskLevels, false) }}</span>
            </div>
        </div>
        <!-- Warnings -->
        <div
            v-if="props.chartData && props.chartData.isSeasonal"
            class="no-hydrograph-data text-bold"
        >
            This gauge is seasonal and will experience periods of inactivity throughout the year.
        </div>
        <div
            v-if="!chartLoading && props.chartData && !hasTwentyFiveYears && !props.chartData.isVirtual"
            class="25-yr-warning text-bold"
        >
            Limited historical gauge data available. Use return period estimations with caution.
        </div>
        <!-- Tooltip -->
        <div
            v-if="tooltipVisible"
            class="streamflow-tooltip"
            :class="props.chartType"
            :style="`top: ${tooltipPosition[1]}px; left: ${tooltipPosition[0]}px;`"
        >
            <div
                v-for="(line, idx) in tooltipText"
                :key="line"
            >
                <div v-if="idx === 0">
                    <p
                        v-if="props.chartData.isVirtual"
                        class="q-mb-none text-bold"
                    >
                        {{ formatDate(new Date(line.value), false, false, true, false).replace('| ', '') }} - {{ formatDate(addDaysToDate(new Date(line.value), 1), false, false, true, false).replace('| ', '') }} {{ props.chartData.gaugeTzAbbr }}
                    </p>
                    <p
                        v-else
                        class="q-mb-none text-bold"
                    >
                        {{ formatDate(new Date(line.value), false, true, showTooltipTime).replace('|', `${props.chartData.gaugeTzAbbr} |`) }}
                    </p>
                    <p
                        v-if="props.chartData.gaugeTzAbbr !== settingStore.userTzAbbr && !props.chartData.isVirtual && showTooltipTime"
                        class="q-mb-none"
                    >
                        {{ formatDate(adjustHoursByTimezone(new Date(line.value), settingStore.timezone, props.chartData.gaugeTz), false, true, !props.chartData.isVirtual).replace('|', `${settingStore.userTzAbbr} |`) }}
                    </p>
                    <p
                        v-else-if="props.chartData.gaugeTzAbbr !== settingStore.userTzAbbr && props.chartData.isVirtual"
                        class="q-mb-none"
                    >
                        {{ formatDate(adjustHoursByTimezone(line.value, settingStore.timezone, props.chartData.gaugeTz), false, false, true, false).replace('| ', '') }} - {{ formatDate(addDaysToDate(adjustHoursByTimezone(line.value, settingStore.timezone, props.chartData.gaugeTz), 1), false, false, true, false).replace('| ', '') }} {{ settingStore.userTzAbbr }}
                    </p>
                </div>
                <div
                    v-else
                    class="streamflow-tooltip-text"
                >
                    <div
                        class="label-legend"
                        :style="`background-color: ${line.color};`"
                    />
                    <b>{{ line.label }}</b>&nbsp;{{ line.value }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import RiskIcon from '@/components/home/sidebar/RiskIcon.vue';
import GaugeMetadata from '@/components/home/asset-drawer/GaugeMetadata.vue';
import VirtualGaugeInfoButton from "@/components/home/sidebar/VirtualGaugeInfoButton.vue";
import { useAssetStore } from '@/stores/assetStore';
import { useSettingStore } from '@/stores/settingStore.js';
import { getGaugeDefaultReturnPeriods, getVirtualGaugeDefaultReturnPeriods } from '@/utils/api.js';
import { addDaysToDate, adjustHoursByTimezone, formatDate } from '@/utils/dateHelpers.js';
import { getSeverity } from '@/utils/styleHelpers.js';
import { convertUnit, convertValue } from '@/utils/unitHelpers.js';
import { computed, onMounted, ref, watch } from 'vue';
import * as d3 from 'd3';

const assetStore = useAssetStore();
const settingStore = useSettingStore();

const props = defineProps({
    chartData: {
        type: Object,
        default: () => {},
    },
    chartType: {
        type: String,
        default: 'drawer',
    },
    reportDate: {
        type: Date,
        default: new Date(),
    },
    gaugeType: {
        type: String,
        default: 'physical'
    },
});

const emit = defineEmits(['close']);

const svgWrap = ref(null);
const svg = ref(null);
const scaleX = ref(null);
const scaleY = ref(null);
const rpAxis = ref(null);
const axisX = ref(null);
const hoverLine = ref(null);
const hoverLinePath = ref(null);
const peakDot = ref(null);
const flowDot = ref(null);
const chartLoading = ref(false);
const startDate = ref();
const endDate = ref();
const g = ref();
const defs = ref();
const peakLine = ref();
const flowLine = ref();

const gaugeDefaultReturnPeriods = ref(null);
const hasTwentyFiveYears = ref(false);

const timelineSvg = ref(null);
const timelineSvgWrap = ref();
const timelinePeakLine = ref(null);
const timelineFlowLine = ref(null);
const timelineG = ref(null);
const timelineScaleX = ref(null);
const timelineScaleY = ref(null);
const timelineHeight = computed(() => {
    return props.chartType === 'report' ? 17 : window.visualViewport.width > 850 ? 22 : 40;
});
const brush = ref();
const timelineBrush = ref();

const tooltipVisible = ref(false);
const tooltipPosition = ref([0, 0]);
const tooltipText = ref('');

const width = 400;
const height = 200;
const margin = {
    top: 10,
    right: 15,
    bottom: 50,
    left: 60,
};

const today = computed(() => {
    if (props.chartType === 'report') {
        return new Date(`${new Date(props.reportDate).toISOString().slice(0, 10)} 00:00:00`);
    }
    return new Date(`${new Date().toISOString().slice(0, 10)} 00:00:00`);
});

const locationUpdateTime = computed(() => {
    return new Date(props.chartData.mostRecentFlowTimestamp);
});
const userUpdateTime = computed(() => {
    return adjustHoursByTimezone(props.chartData.mostRecentFlowTimestamp, settingStore.timezone, props.chartData.gaugeTz);
});

// these computeds handle report and drawer conditions
const longRiskFlow = computed(() => {
    if (props.chartType === 'report') return Math.max(props.chartData.highestForecastFlow, shortRiskFlow.value) || '';
    // Handle value == 0 cases
    if ('highestFlowLastTwoHours' in props.chartData) return Math.max(props.chartData.highestFlowLastTwoHours, shortRiskFlow.value);
    if ('highestForecastFlow' in props.chartData) return Math.max(props.chartData.highestForecastFlow, shortRiskFlow.value);
    return null;
});
const shortRiskFlow = computed(() => {
    if (props.chartType === 'report' ) return props.chartData.riskLevelMostRecent || '';
    return props.chartData.mostRecentFlow;
});
const longRiskType = computed(() => {
    if (props.chartType === 'report') return "Maximum Forecasted Streamflow";
    return props.chartData.isVirtual ? 'Max Forecasted Streamflow' : 'Highest flow in last 2hrs';
});
const shortRiskType = computed(() => {
    if (props.chartType === 'report') return "Current Day Streamflow";
    return props.chartData.isVirtual ? "Current Streamflow Estimate" : "Current flow";
});
const longRiskLevel = computed(() => {
    if (props.chartType === 'report') props.chartData.riskLevelHighestForecast || '';
    return props.chartData.riskLevelLastTwoHours || props.chartData.riskLevelHighestForecast;
});
const shortRiskLevel = computed(() => {
    if (props.chartType === 'report') return props.chartData.maxSevenDaysRiskLevel || '';
    return props.chartData.riskLevelMostRecent;
});

const chartOptions = ref({
    showFlow: true,
    showPeak: true,
    riskThresholds: [],
    returnPeriods: [],
    showReturnPeriods: false,
    showAllRiskLevels: false,
});

const tableContent = [
    {
        id: 'long',
        header: longRiskType.value,
        value: longRiskFlow.value || longRiskFlow.value === 0 ? `${longRiskFlow.value.toFixed(2)} ${convertUnit('m³/s')}` : 'Not Available'
    },
    {
        id: 'short',
        header: shortRiskType.value,
        value: shortRiskFlow.value || shortRiskFlow.value === 0 ? `${shortRiskFlow.value.toFixed(2)} ${convertUnit('m³/s')}` : 'Not Available'
    }
];

const allViewOptions = [
    {
        value: 'lastSevenDayFlowTimeSeries',
        label: 'Last 7 Days',
        text: 'Seven Day Flow',
        position: 1,
    },
    {
        value: 'lastDayFlowTimeSeries',
        label: 'Last Day',
        text: 'Daily Flow',
        position: 0,
    },
    {
        value: 'lastThirtyDaysTimeSeries',
        label: 'Last Month',
        text: 'Thirty Day Flow',
        position: 2,
    },
    {
        value: 'lastYearTimeSeries',
        label: 'Last Year',
        text: 'Annual Flow',
        position: 3,
    },
    {
        value: 'lastFiveYearsTimeSeries',
        label: 'Last 5 Years',
        text: 'Five Year Flow',
        position: 4,
    },
];
const allReportViewOptions = [
    {
        value: 'sevenDayTimeSeries',
        label: '7 Day Series',
        text: 'Seven Day Flow',
        position: 3,
    },
    {
        value: 'thirtyDayTimeSeries',
        label: '30 Day Series',
        text: 'Thirty Day Flow',
        position: 4,
    },
];
const virtualGaugeViewOptions = [
    {
        value: 'report',
        label: 'Last 10 days + Forecast',
        text: 'Virtual Gauge Flow',
        position: 0,
    },
    {
        value: 'forecastOnly',
        label: 'Forecast Only',
        text: 'Virtual Gauge Flow',
        position: 2,
    },
];

const virtualGaugeFullViewOption = {
    value: 'fullData',
    label: 'All Available Data',
    text: 'Virtual Gauge Flow',
    position: 1,
};

const viewTypeOptions = ref(props.chartData.hasReportData ? allReportViewOptions : props.chartData.isVirtual ? [] : allViewOptions);
const viewType = ref(allViewOptions[1]);

const enableLayerReset = computed(() => {
    let isEnabled = chartOptions.value.showReturnPeriods;
    if (chartOptions.value.riskThresholds.find(rp => rp.showRiskThreshold)) isEnabled = true;
    if (!chartOptions.value.showFlow || !chartOptions.value.showPeak) isEnabled = true;
    if (startDate.value && (new Date(startDate.value).getTime() !== new Date(selectedChartData.value[0].datetime).getTime())) isEnabled = true;
    if (endDate.value && (new Date(endDate.value).getTime() !== new Date(selectedChartData.value[selectedChartData.value.length - 1].datetime).getTime())) isEnabled = true;
    return isEnabled;
});

const getRiskLevelText = (riskLevel, showRp = true, showRl = true) => {
    const getOnMyLevel = settingStore.gaugeLevelByNumber(riskLevel)
    if (showRp) {
        if (showRl) {
            return `${riskLevel}: ${getOnMyLevel.riskLabel} (${riskLevel === 1 ? '<' : '>'}${riskLevel === 1 ? getOnMyLevel.upperBound : getOnMyLevel.lowerBound} year Return Period)`;
        } else {
            return `${getOnMyLevel.riskLabel} (${riskLevel === 1 ? '<' : '>'}${riskLevel === 1 ? getOnMyLevel.upperBound : getOnMyLevel.lowerBound}yr RP)`;
        }
    } else {
        if (showRl) {
            return `${riskLevel}: ${getOnMyLevel.riskLabel}`;
        } else {
            return `${getOnMyLevel.riskLabel}`;
        }
    }
};

const resetChartLayers = () => {
    chartOptions.value.showReturnPeriods = false;
    chartOptions.value.showAllRiskLevels = false;
    toggleAllRiskLevels();
    chartOptions.value.showFlow = true;
    chartOptions.value.showPeak = true;
    startDate.value = new Date(selectedChartData.value[0].datetime);
    endDate.value = new Date(selectedChartData.value[selectedChartData.value.length - 1].datetime);

    updateChart();
};

const selectedChartData = computed(() => {
    if (props.chartData.isVirtual) {
        let myData = props.chartData.timeSeries;
        if (viewType.value.value === 'forecastOnly') {
            myData = myData.filter(el => new Date(el.datetime) >= addDaysToDate(props.reportDate, -1));
        } else if (viewType.value.value === 'report') {
            myData = myData.filter(el => new Date(el.datetime) >= addDaysToDate(props.reportDate, -11));
        }
        myData.forEach((el, idx) => {
            el.idx = idx;
        });
        return myData;
    } else {
        return props.chartData[viewType.value.value];
    }
});

watch(() => selectedChartData.value, () => {
    setDateRanges(selectedChartData.value);
    updateChart();
});

watch(() => viewType.value, () => {
    assetStore.streamflowChartOptions.viewMode = viewType.value.value;
    if (viewType.value.value === 'lastSevenDayFlowTimeSeries' || viewType.value.value === 'lastDayFlowTimeSeries') {
        chartOptions.value.showFlow = true;
        chartOptions.value.showPeak = true;
    }
});

watch(() => chartOptions.value, () => {
    if (chartLoading.value) return;
    chartOptions.value.riskThresholds.forEach(el => assetStore.streamflowChartOptions[`level-${el.riskLevel}`] = el.showRiskThreshold);
    assetStore.streamflowChartOptions.showAllRiskLevels = chartOptions.value.showAllRiskLevels;
    assetStore.streamflowChartOptions.showReturnPeriods = chartOptions.value.showReturnPeriods;
    updateChart();
}, { deep: true });

const toggleAllRiskLevels = () => {
    // Flux-3656 set all levels, not just ones this (virtual) gauge has
    settingStore.gaugeLevels.forEach(el => assetStore.streamflowChartOptions[`level-${el.riskLevel}`] = chartOptions.value.showAllRiskLevels);
    chartOptions.value.riskThresholds.forEach(el => el.showRiskThreshold = chartOptions.value.showAllRiskLevels);
};

const activeChartRange = computed(() => {
    return selectedChartData?.value;
});

const yMax = computed(() => {
    let activeRps = [];

    if (chartOptions.value.showReturnPeriods) {
        const highestYear = Math.max(...chartOptions.value.returnPeriods.filter(el => typeof el.returnPeriodYear === 'number').map(el => el.returnPeriodYear));
        activeRps.push(chartOptions.value.returnPeriods.find(el => el.returnPeriodYear === highestYear)?.value);
    }

    chartOptions.value.riskThresholds.forEach(el => {
        if (el.showRiskThreshold) {
            activeRps.push(el.value || el.lowerBound);
        }
    });
    return convertValue(Math.max(0.1, ...selectedChartData.value.map(el => Math.max(el?.peak || 0, el.flow)), ...activeRps), 'm³/s');
});

const timelineYMax = computed(() => {
    return Math.max(...selectedChartData.value.map(el => Math.max(el?.peak || 0, el.flow)));
});

const getDefaultReturnPeriods = async (gaugeId) => {
    chartLoading.value = true;
    gaugeDefaultReturnPeriods.value = [];
    if (props.chartData.isVirtual) {
        gaugeDefaultReturnPeriods.value = await getVirtualGaugeDefaultReturnPeriods(gaugeId).then(res => res.returnPeriods);
        gaugeDefaultReturnPeriods.value.forEach((returnPeriod, idx) => {
            let returnPeriodYear;
            let optionLabel;
            if (idx === 0) {
                returnPeriodYear = gaugeDefaultReturnPeriods.value[idx + 1].returnPeriodYear;
                optionLabel = `Less than ${gaugeDefaultReturnPeriods.value[1].returnPeriodYear} yr RP`;
            } else if (idx === gaugeDefaultReturnPeriods.value.length - 1) {
                returnPeriodYear = returnPeriod.returnPeriodYear;
                optionLabel = `${returnPeriod.returnPeriodYear}+ yr RP`.replace('> ', '');
            } else {
                returnPeriodYear = gaugeDefaultReturnPeriods.value[idx + 1].returnPeriodYear;
                optionLabel = `${returnPeriod.returnPeriodYear}-${gaugeDefaultReturnPeriods.value[idx + 1].returnPeriodYear} yr RP`;
            }
            chartOptions.value.returnPeriods.push({
                returnPeriodYear,
                value: returnPeriod.riskLevelUpperBound,
                label: optionLabel,
                showReturnPeriod: assetStore.streamflowChartOptions[returnPeriod.returnPeriodYear],
            });
        });
    } else {
        gaugeDefaultReturnPeriods.value = await getGaugeDefaultReturnPeriods(gaugeId);
        getGaugeTenYearData(gaugeDefaultReturnPeriods.value);

        // if year 200 RP is included
        const rp200 = gaugeDefaultReturnPeriods.value.returnPeriods.find((el) => el.returnPeriodYear === 200);
        if (rp200) {
            gaugeDefaultReturnPeriods.value.returnPeriods.push({
                returnPeriodYear: '> 200',
                value: null,
                showReturnPeriod: false,
            });
        }

        // if the gauge has no default return periods
        if (!('message' in gaugeDefaultReturnPeriods.value)) {
            gaugeDefaultReturnPeriods.value.returnPeriods.forEach((returnPeriod, idx) => {
                let optionLabel;
                if (idx === 0) {
                    optionLabel = `Less than ${returnPeriod.returnPeriodYear} yr RP`;
                } else if (idx === gaugeDefaultReturnPeriods.value.returnPeriods.length - 1) {
                    optionLabel = `${returnPeriod.returnPeriodYear}+ yr RP`.replace('> ', '');
                } else {
                    optionLabel = `${gaugeDefaultReturnPeriods.value.returnPeriods[idx - 1].returnPeriodYear}-${returnPeriod.returnPeriodYear} yr RP`;
                }
                chartOptions.value.returnPeriods.push({
                    returnPeriodYear: returnPeriod.returnPeriodYear,
                    value: returnPeriod.value,
                    label: optionLabel,
                    showReturnPeriod: assetStore.streamflowChartOptions[returnPeriod.returnPeriodYear],
                });
            });
        }
    }
    chartLoading.value = false;
};

const getGaugeTenYearData = (data) => {
    // if true, RP was created using at least 25 years of data
    // if false, RP was created using less than 25, but more than 10 years of data, or there is no RP.
    hasTwentyFiveYears.value = data.hasReturnPeriods ? data.has25yrsOfGaugeData : true;
};

// D3 Functions
const waitForElementToExist = (selector) => {
    return new Promise(resolve => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                observer.disconnect();
                resolve(document.querySelector(selector));
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
};

onMounted(async () => {
    await waitForElementToExist(`.${props.chartType}-mode`).then(() => {
        svgWrap.value = document.querySelector(`.${props.chartType}-mode`);;
    });
    svg.value = d3.select(svgWrap.value.querySelector('svg'));
    svg.value.attr('viewBox', `0 0 420 ${height}`);

    timelineSvgWrap.value = document.querySelector(`.streamflow-timeline-${props.chartType}`);
    timelineSvg.value = d3.select(timelineSvgWrap.value);
    timelineSvg.value.attr('viewBox', `0 0 420 ${timelineHeight.value}`);
    let availableChartOptions = [];
    // For Assets with Virtual Gauges
    if (props.chartData.isVirtual) {
        if (props.chartType !== 'log') {
            availableChartOptions = virtualGaugeViewOptions;
        }
        if (props.chartType !== 'report') {
            availableChartOptions.push(virtualGaugeFullViewOption);
        }
    } else {
        // Get the available chart options
        if (props.chartData.hasRecentData) {
            availableChartOptions.push(...allViewOptions.slice(0, 2));
        }
        if (props.chartData.hasHistoricalData) {
            availableChartOptions.push(...allViewOptions.slice(2, allViewOptions.length));
        }
        if (props.chartData.hasReportData) {
            if ('sevenDayTimeSeries' in props.chartData) {
                availableChartOptions.push(allReportViewOptions[0]);
            }
            if ('thirtyDayTimeSeries' in props.chartData) {
                availableChartOptions.push(allReportViewOptions[1]);
            }
        }
        // each time series needed to have its index set initially for use in the timeline chart
        availableChartOptions.forEach(key => {
            props.chartData[key.value].forEach((el, idx) => el.idx = idx);
        });
    }
    viewTypeOptions.value = availableChartOptions;

    chartOptions.value.showAllRiskLevels = assetStore.streamflowChartOptions.showAllRiskLevels;
    if (props.chartData.hasReturnPeriods || props.chartData.isVirtual) {
        chartOptions.value.showReturnPeriods = assetStore.streamflowChartOptions.showReturnPeriods;
    } else {
        chartOptions.value.showReturnPeriods = false;
    }

    // Get previously used chart mode if available
    const myViewOption = availableChartOptions.find(el => el.value === assetStore.streamflowChartOptions.viewMode);
    if (myViewOption) {
        viewType.value = myViewOption;
    } else {
        viewType.value = availableChartOptions[0];
    }

    setDateRanges(selectedChartData.value);

    // Get Risk Level values
    props.chartData[props.chartData.isVirtual ? 'riskLevels' : 'returnPeriods'].forEach(el => {
        chartOptions.value.riskThresholds.push({
            riskLevel: el.riskLevel,
            value: el.riskLevelUpperBound,
            lowerBound: el.riskLevelLowerBound,
            showRiskThreshold: assetStore.streamflowChartOptions[`level-${el.riskLevel}`],
            color: settingStore.gaugeLevelByNumber(el.riskLevel).color,
        });
    });

    // Get return periods
    await getDefaultReturnPeriods(props.chartData.gaugeId);
    updateChart();
});

const init = async () => {
    if (!selectedChartData.value) return;
    if (svg.value) {
        svg.value.selectAll("*").remove()
    }

    g.value = svg.value.append("g")
        .attr("transform", `translate(0, ${margin.top})`);

    defs.value = g.value.append('defs');
    defs.value.append('clipPath')
        .attr('id', `streamflow-box-clip-${props.chartType}`)
        .append('rect')
        .attr('width', width - margin.right - margin.left)
        .attr('height', height - margin.bottom)
        .attr('transform', `translate(${margin.left}, 0)`)

    setAxisX();
    setAxisY();

    if (svg.value) {
        addTimelineDistinction();
        addRiskThresholdLines();
        addReturnPeriodLines();
        addStreamflowLine();
        bindTooltipHandlers();
    }
    initBrush();
};

const initBrush = () => {
    // Add Brush
    brush.value = d3.brushX()
        .extent([[margin.left, 0], [width - margin.right, height - margin.bottom + margin.top]])
        .on("end", brushed)

    svg.value.append("g")
        .attr('class', `brush brush-main-${props.chartType}`)
        .call(brush.value)

    function brushed(event) {
        const selection = event.selection;
        if (!event.sourceEvent || !selection) return;
        let [x0, x1] = selection.map(d => Math.floor(scaleX.value.invert(d)));
        startDate.value = new Date(x0);
        endDate.value = new Date(x1);

        d3.select(`.brush-${props.chartType}`).call(brush.value.move, [startDate.value, endDate.value].map(timelineScaleX.value));

        init();
    }
};

// Timeline Stuff
const initTimeline = () => {
    if (!selectedChartData.value) return;
    if (timelineSvg.value) {
        timelineSvg.value.selectAll('*').remove();
    }
    timelineG.value = timelineSvg.value.append('g').attr('transform', `translate(${margin.left}, 0)`);

    // Makes the outline
    timelineSvg.value.append("rect")
        .attr("x", margin.left)
        .attr("y", 0)
        .attr("height", timelineHeight.value)
        .attr("width", width - (margin.left + margin.right))
        .attr("class", "overlay")
        .style("stroke", 'grey')
        .style("fill", "none")
        .style("stroke-width", '1px');

    // X Axis
    timelineScaleX.value = d3.scaleTime()
        .domain([new Date(selectedChartData.value[0].datetime), new Date(selectedChartData.value[selectedChartData.value.length - 1].datetime)])
        .range([margin.left, width - margin.right])

    // Y Axis
    timelineScaleY.value = d3.scaleLinear()
        .domain([0, timelineYMax.value * 1.1])
        .range([timelineHeight.value, 0])

    // Only show this if the gauge is a virtual gauge
    if (props.chartData.isVirtual && props.chartType !== 'log') {
        // Add Values
        const givenData = [];
        let futureCount = 0;

        activeChartRange.value.forEach(el => {
            givenData.push(el)
            if (new Date(el.datetime) >= today.value) {
                futureCount += 1;
            }
        });

        if (futureCount > 1 && !isNaN(timelineScaleX.value(today.value))) {
            // Add today's line
            timelineSvg.value
                .append("line")
                .attr('class', `today-line streamflow-clipped-${props.chartType}`)
                .attr("x1", timelineScaleX.value(today.value))
                .attr("y1", 1)
                .attr("x2", timelineScaleX.value(today.value))
                .attr("y2", height - 2)
                .style("stroke-width", 1);
            // Shade past data
            timelineSvg.value
                .append("rect")
                .attr('class', `bar past streamflow-clipped-${props.chartType}`)
                .attr("x", margin.left)
                .attr("y", 1)
                .attr("height", timelineHeight.value - 1)
                .attr("width", Math.max(0, endDate.value >= today.value && timelineScaleX.value(today.value) > 0 ? timelineScaleX.value(today.value) : width) - margin.left)
        }
    }

    // Add streamflow values to timeline
    if (chartOptions.value.showFlow) {
        timelineFlowLine.value = timelineSvg.value.append("g")
            .append("path")
            .datum(selectedChartData.value)
            .attr("fill", "none")
            .attr("stroke", 'black')
            .attr("stroke-width", 1)
            .attr("class", `line timeline streamflow-clipped-${props.chartType}`)
            .attr("d", d3
                .line()
                .x((d) => timelineScaleX.value(new Date(d.datetime)))
                .y((d) => timelineScaleY.value(d.flow))
                .defined((d) => d.flow !== null && !isNaN(d.flow) && !isNaN(timelineScaleX.value(new Date(d.datetime))))
            );
    }

    if (chartOptions.value.showPeak) {
        timelinePeakLine.value = timelineSvg.value.append("g")
            .append("path")
            .datum(selectedChartData.value)
            .attr("fill", "none")
            .attr("stroke", '#32617C')
            .attr("stroke-width", 1)
            .attr("class", `line timeline streamflow-clipped-${props.chartType}`)
            .attr("d", d3
                .line()
                .x((d) => timelineScaleX.value(new Date(d.datetime)))
                .y((d) => timelineScaleY.value(d.peak))
                .defined((d) => d.peak !== null && !isNaN(d.peak))
            );
    }

    initTimelineBrush();
};

const initTimelineBrush = () => {
    if (!startDate.value) return;
    timelineBrush.value = d3.brushX()
        .extent([[margin.left, 0], [width - margin.right, timelineHeight.value]])
        .on('start brush end', brushed)

    timelineSvg.value.append("g")
        .attr('class', `brush-${props.chartType}`)
        .call(timelineBrush.value)
        .call(timelineBrush.value.move, [startDate.value, endDate.value].map(timelineScaleX.value))

    function brushed(event) {
        const selection = event.selection;
        if (!event.sourceEvent || !selection) return;
        let [x0, x1] = selection.map(d => Math.floor(timelineScaleX.value.invert(d)));
        startDate.value = new Date(x0);
        endDate.value = new Date(x1);
        d3.select(`.brush-${props.chartType}`).call(brush.value.move, [startDate.value, endDate.value].map(timelineScaleX.value));
        init();
    }
};

// Basic Chart Stuff
const setDateRanges = (givenData) => {
    if (!givenData) return;
    startDate.value = new Date(givenData[0].datetime);
    endDate.value = new Date(givenData[givenData.length - 1].datetime);
};
const gxAxisGrid = ref();
const gyAxisGrid = ref();
const axisY = ref();

const setAxisX = () => {
    scaleX.value = d3.scaleTime()
        .domain([startDate.value, endDate.value])
        .range([margin.left, width - margin.right])

    if (axisX.value) axisX.value.remove();
    axisX.value = g.value.append("g")
        .attr("transform", `translate(0, ${height - margin.bottom})`)
        .call(
            d3.axisBottom(scaleX.value)
                .ticks(!props.chartData.isVirtual || ((endDate.value - startDate.value) / 86400000) > 4 ? 5 : d3.timeDay.every(1))
                .tickFormat((date) => {
                    if (props.chartData.isVirtual) return d3.timeFormat("%b %d")(date)
                    if (d3.timeYear(date) < date) {
                        if (d3.timeMonth(date) < date && d3.timeFormat("%I:%M %p")(date) !== '12:00 AM') {
                            const axisLabel = d3.timeFormat("%I:%M %p")(date)
                            if (axisLabel.charAt(0) === '0') return axisLabel.slice(1)
                            return axisLabel
                        }
                        return d3.timeFormat("%b %d")(date)
                    } else {
                        return d3.timeFormat("%Y")(date)
                    }
                })
        );
    axisX.value.selectAll("text")
        .style("text-anchor", "end")
        .style("font-size", "0.9em")
        .attr("dx", "-0.8em")
        .attr("dy", "0.15em")
        .attr("transform", "rotate(-50)");

    gxAxisGrid.value = g.value.append('g')
        .attr("transform", `translate(0, ${margin.top})`)
        .attr('class', 'x axis-grid')
        .call(
            d3.axisBottom(scaleX.value)
                .ticks(5)
                .tickSize(height - (margin.bottom + margin.top))
                .tickFormat('')
        )
};

const setAxisY = () => {
    scaleY.value = d3.scaleLinear()
        .domain([0, yMax.value * 1.1])
        .range([height - margin.bottom, margin.top])

    if (axisY.value) axisY.value.remove();

    // adds the y-axis grid lines to the chart.
    axisY.value = g.value.append("g")
        .attr("transform", `translate(${margin.left}, 0)`)
        .attr('class', 'y-axis')
        .call(d3.axisLeft(scaleY.value).ticks(5, yMax.value > 1 ? ',.1f' : ',.01f'))

    gyAxisGrid.value = g.value.append("g")
        .attr("class", "y axis-grid")
        .attr('transform', `translate(${margin.left}, 0)`)
        .call(d3.axisLeft(scaleY.value)
            .ticks(5)
            .tickSize(-width + (margin.left + margin.right))
            .tickFormat("")
        )
        .attr('stroke-opacity', 0.5)
};

const addRiskThresholdLines = () => {
    d3.selectAll(`.threshold-${props.chartType}`).remove();

    chartOptions.value.riskThresholds.forEach((rp, idx) => {
        if (rp.showRiskThreshold) {
            const riskLevelLine = g.value.append('g')
                .attr('class', `threshold-${props.chartType} return-period`)

            const lastIdx = idx - 1;
            const isLastIdxInRPs = idx === chartOptions.value.riskThresholds.length - 1;

            // Special case where we can show a region above the 200 year return period
            if (isLastIdxInRPs) {
                riskLevelLine.append('rect')
                    .attr('class', `threshold streamflow-clipped-${props.chartType}`)
                    .style('fill', `${rp.color}50`)
                    .attr('x', scaleX.value(startDate.value))
                    .attr('y', margin.top)
                    .attr("width", Math.max(0, width - (margin.left + margin.right)))
                    .attr("height", () => scaleY.value(convertValue(rp.lowerBound, 'm³/s')) - margin.top)
            } else {
                riskLevelLine.append('rect')
                    .attr('class', `threshold streamflow-clipped-${props.chartType}`)
                    .style('fill', `${rp.color}50`)
                    .attr('x', scaleX.value(startDate.value))
                    .attr('y', scaleY.value(convertValue(rp.value, 'm³/s')))
                    .attr("width", Math.max(0, width - (margin.left + margin.right)))
                    .attr("height", () => {
                        return lastIdx === -1 ? scaleY.value(0) : scaleY.value(convertValue(chartOptions.value.riskThresholds[lastIdx].value, 'm³/s')) -
                        scaleY.value(convertValue(rp.value, 'm³/s'))
                    })
            }
            if (rp.lowerBound) {
                riskLevelLine.append('line')
                    .attr('class', `threshold return-period streamflow-clipped-${props.chartType}`)
                    .attr('x1', scaleX.value(startDate.value))
                    .attr('y1', scaleY.value(convertValue(rp.lowerBound, 'm³/s')))
                    .attr('x2', scaleX.value(endDate.value))
                    .attr('y2', scaleY.value(convertValue(rp.lowerBound, 'm³/s')) )
                    .attr('stroke', rp.color)
                    .attr('stroke-width', '1')
            }
        }
    });
};

const addReturnPeriodLines = () => {
    d3.selectAll(`.rp-${props.chartType}`).remove();

    if (chartOptions.value.showReturnPeriods) {
        chartOptions.value.returnPeriods.forEach((returnPeriod, idx) => {
            if (idx !== chartOptions.value.returnPeriods.length - 1) {
                g.value.append('line')
                    .attr('class', `rp-${props.chartType} return-period dashed streamflow-clipped-${props.chartType}`)
                    .attr('x1', scaleX.value(startDate.value))
                    .attr('y1', scaleY.value(convertValue(returnPeriod.value, 'm³/s')))
                    .attr('x2', scaleX.value(endDate.value))
                    .attr('y2', scaleY.value(convertValue(returnPeriod.value, 'm³/s')))
                    .attr('stroke', '#444')
                    .attr('stroke-width', '1')
            }
        });

        const customFormat = (val) => {
            return chartOptions.value.returnPeriods.find((el) => val === convertValue(el.value, 'm³/s')).returnPeriodYear;
        }

        rpAxis.value = g.value.append("g")
            .call(
                d3.axisRight(scaleY.value)
                    .tickFormat(customFormat)
                    .tickSizeOuter(0)
                    .ticks(chartOptions.value.length)
                    .tickValues(chartOptions.value.returnPeriods.filter(el => el.value).map(el => convertValue(el.value, 'm³/s')))
            )
            .attr('class', `rp-${props.chartType} right-axis`)
            .attr('transform', `translate(${width - margin.right}, -0.5)`)
    }
};

const addStreamflowLine = () => {
    if (flowLine.value) d3.selectAll(`.line-${props.chartType}`).remove();
    if (chartOptions.value.showFlow) {
        flowLine.value = g.value
            .append("path")
            .datum(activeChartRange.value)
            .attr("fill", "none")
            .attr("stroke", 'black')
            .attr("stroke-width", 1)
            .attr("class", `flow streamflow-clipped-${props.chartType}`)
            .attr("d", d3
                .line()
                .x((d) => isNaN(scaleX.value(new Date(d.datetime))) ? 0 : scaleX.value(new Date(d.datetime)))
                .y((d) => scaleY.value(convertValue(d.flow, 'm³/s')))
                .defined((d) => d.flow !== null && !isNaN(d.flow))
            );
    }

    if (chartOptions.value.showPeak && activeChartRange.value.filter(el => el.peak).length > 0) {
        peakLine.value = g.value
            .append("path")
            .datum(activeChartRange.value)
            .attr("fill", "none")
            .attr("stroke", '#32617C')
            .attr("stroke-width", 1)
            .attr("class", `current streamflow-clipped-${props.chartType}`)
            .attr("d", d3
                .line()
                .x((d) => scaleX.value(new Date(d.datetime)))
                .y((d) => scaleY.value(convertValue(d?.peak, 'm³/s')))
                .defined((d) => d.peak !== null && !isNaN(d.peak))
            );
    }
};

const addTimelineDistinction = () => {
    if (!props.chartData.isVirtual) return;
    if (props.chartType === 'log') return;

    if (startDate.value <= today.value) {
        // Add line to Today
        if (endDate.value >= today.value) {
            g.value
                .append("line")
                .attr('class', 'today-line')
                .attr("x1", scaleX.value(today.value) - margin.left) // Max was right!
                .attr("y1", 0)
                .attr("x2", scaleX.value(today.value) - margin.left)
                .attr("y2", height - margin.top - margin.bottom)
                .attr('transform', `translate(${margin.left}, ${margin.top})`)
                .style("stroke-width", 1);
        }

        // Shade past data
        g.value
            .append("rect")
            .attr('class', "bar past")
            .attr("x", 0)
            .attr("y", 0)
            .attr('transform', `translate(${margin.left}, ${margin.top})`)
            .attr("height", height - margin.top - margin.bottom)
            .attr("width", Math.max(0, endDate.value >= today.value && scaleX.value(today.value) > 0 ? scaleX.value(today.value) - margin.left : width - margin.left - margin.right))
    }

    // Add Forecast text
    g.value
        .append("text")
        .attr("class", `text today streamflow-clipped-${props.chartType}`)
        .attr("x", scaleX.value(today.value) - margin.left)
        .attr("dx", "64px")
        .attr("y", 10)
        .attr("dy", "1.25em")
        .text("Forecast ▸")
        .style('font-size', '0.82em')

    if (props.chartType !== 'report') {
        g.value
            .append("text")
            .attr("class", `text today streamflow-clipped-${props.chartType}`)
            .attr("x", scaleX.value(today.value) - margin.left)
            .attr("dx", "22px")
            .attr("y", 10)
            .attr("dy", "1.25em")
            .text("◂ Past")
            .style('font-size', '0.82em')
    }
};

// Tooltip Stuff
const bindTooltipHandlers = () => {
    svg.value.on('mousemove', (ev) => tooltipMouseMove(ev))
    svg.value.on('mouseout', tooltipMouseOut)
};

const tooltipMouseMove = (event) => {
    const [gX, gY] = d3.pointer(event, g.value.node());
    if (gX < 0 || gX > width || gY < 0 || gY > height) {
        tooltipMouseOut();
        return;
    }
    const positionX = () => {
        if (props.chartType === 'tooltip') {
            if (window.screen.width < 850) {
                // Rightmost values, left-aligned tooltip
                if (gX > 200) return event.offsetX - 200;
                // Central values, centered tooltip
                if (gX > 120) return event.offsetX - 100;
            }
            // Leftmost values, right-aligned tooltip
            return event.offsetX - 30;
        }
        if (window.screen.width < 850) {
            // Rightmost values, left-aligned tooltip
            if (gX > 200) return event.x - 200;
            // Central values, centered tooltip
            if (gX > 120) return event.x - 100;
        }
        // Leftmost values, right-aligned tooltip
        return event.x;
    };
    const positionY = () => {
        if (props.chartType === 'tooltip') {
            return event.offsetY + 200;
        }
        return event.y;
    }
    tooltipPosition.value = [
        positionX(), positionY()
    ];

    const date = scaleX.value.invert(gX);
    if (date < startDate.value) return;

    const bisect = d3.bisector(d => new Date(d.datetime)).center;
    const idx = bisect(activeChartRange.value, date);
    const data = activeChartRange.value[idx];

    addHoverEffects(event);

    const tooltipTextX = [];
    if (data) {
        chartOptions.value.riskThresholds.forEach(el => {
            if (el.showRiskThreshold && el.riskLevel > 1) {
                tooltipTextX.unshift({
                    type: 'rp',
                    label: `Level ${el.riskLevel} - ${settingStore.gaugeLevelByNumber(el.riskLevel).riskLabel}:`,
                    value: `${convertValue(el.lowerBound, 'm³/s').toFixed(2)} ${convertUnit('m³/s')}`,
                    color: el.color
                });
            }
        });
        if (data.peak && chartOptions.value.showPeak) {
            tooltipTextX.unshift({
                type: 'line',
                label: 'Peak:',
                value: `${convertValue(data.peak, 'm³/s').toFixed(2)} ${convertUnit('m³/s')}`,
                color: '#32617C'
            });
        }
        if (chartOptions.value.showFlow) {
            tooltipTextX.unshift({
                type: 'line',
                label: props.chartData.isVirtual ? 'Average Flow' : viewType.value.value === 'lastSevenDayFlowTimeSeries' || viewType.value.value === 'lastDayFlowTimeSeries' ? 'Flow:' : 'Average:',
                value: data.flow || data.flow === 0 ? `${convertValue(data.flow, 'm³/s').toFixed(2)} ${convertUnit('m³/s')}` : 'Not Available',
                color: 'black'
            });
        }
        tooltipTextX.unshift({
            type: 'date',
            value: data.datetime
        });
        tooltipText.value = tooltipTextX;
        tooltipVisible.value = true;
    }
};

const addHoverEffects = (event) => {
    const [gX] = d3.pointer(event, g.value.node());
    const date = scaleX.value.invert(gX);
    if (date < startDate.value) return;
    const bisect = d3.bisector(d => new Date(d.datetime)).center;
    const idx = bisect(activeChartRange.value, date);

    if (hoverLine.value) hoverLine.value.remove();

    if (activeChartRange.value[idx]) {
        hoverLine.value = g.value.append('g')
            .attr('class', 'hovered')

        hoverLinePath.value = hoverLine.value.append('line')
            .attr('class', `hovered dashed streamflow-clipped-${props.chartType}`)
            .attr('x1', scaleX.value(new Date(activeChartRange.value[idx].datetime)))
            .attr('y1', margin.top)
            .attr('x2', scaleX.value(new Date(activeChartRange.value[idx].datetime)))
            .attr('y2', height - margin.bottom)
            .attr('stroke', '#eee')
            .attr('stroke-width', '2')
    }

    if (peakDot.value) {
        peakDot.value.remove();
    }
    if (flowDot.value) {
        flowDot.value.remove();
    }

    if (activeChartRange.value[idx].peak && chartOptions.value.showPeak) {
        peakDot.value = g.value.append('g')
            .attr('class', 'dot');

        peakDot.value.append('circle')
            .attr('class', `dot streamflow-clipped-${props.chartType}`)
            .attr("r", 3)
            .attr('cy', scaleY.value(convertValue(activeChartRange.value[idx].peak, 'm³/s')))
            .attr('cx', scaleX.value(new Date(activeChartRange.value[idx].datetime)))
            .attr('fill', '#32617C')
    }

    if (activeChartRange.value[idx].flow && chartOptions.value.showFlow) {
        flowDot.value = g.value.append('g')
            .attr('class', 'dot');

        flowDot.value.append('circle')
            .attr('class', `dot streamflow-clipped-${props.chartType}`)
            .attr("r", 3)
            .attr('cy', scaleY.value(convertValue(activeChartRange.value[idx].flow, 'm³/s')))
            .attr('cx', scaleX.value(new Date(activeChartRange.value[idx].datetime)))
            .attr('fill', 'rgba(0, 0, 0, 0.7)')
    }
};

const tooltipMouseOut = () => {
    tooltipVisible.value = false;
    svg.value.selectAll('.hovered').remove();
    if (peakDot.value) {
        peakDot.value.remove();
    }
    if (flowDot.value) {
        flowDot.value.remove();
    }
};

const showTooltipTime = computed(() => {
    return (viewType.value.value === 'sevenDayTimeSeries' && selectedChartData.value.length > 8) || viewType.value.label === 'Last 7 Days' || viewType.value.label === 'Last Day';
});

const updateChart = () => {
    init();
    initTimeline();
};
</script>

<style lang="scss">
#streamflow-chart-container {
    display: flex;
    align-items: center;
    margin-left: 1rem;
}

#streamflow-timeline-container {
    display: flex;
    margin-left: 1rem;
}

.svg-wrap {
    width: 100%;
}

.chart-options-container {
    display: flex;

    .layers-button {
        color: #000000DE;

        @media screen and (min-width: $mobile-breakpoint) {
            margin-left: 0.5em;
        }
    }

    @media screen and (max-width: $mobile-breakpoint) {
        display: grid;
        grid-template-columns: 50% 50%;
        grid-template-rows: auto;
        grid-template-areas:
            "range range"
            "layers reset";

        .range-select {
            grid-area: range;
            margin-bottom: 0.5em;
        }
        .layers-button {
            grid-area: layers;
        }
        .reset-button {
            grid-area: reset;
        }
    }
}

.q-field--outlined .q-field__control::before {
  border: 1px solid $grey !important;
}
.q-btn--outline:before {
    border: 1px solid $grey;
}

.axis-grid {
    pointer-events: none;

    line {
        stroke: rgb(201, 201, 201);
    }
    path {
        visibility: hidden;
    }
}

.dashed{
   stroke-dasharray: 5,6;
}

// elements clipped by the clip-path rectangle
.streamflow-clipped-drawer {
    clip-path: url('#streamflow-box-clip-drawer');
}
.streamflow-clipped-tooltip {
    clip-path: url('#streamflow-box-clip-tooltip');
}
.streamflow-clipped-report {
    clip-path: url('#streamflow-box-clip-report');
}
.streamflow-clipped-log {
    clip-path: url('#streamflow-box-clip-log');
}

.return-period-label {
    cursor: default;
}

.year-color {
    .q-checkbox__bg, .q-icon {
        color: #000000;
    }
}

.level-87ceeb {
    .q-checkbox__bg, .q-icon {
        color: #87ceeb;
    }
}
.level-6f8ee8 {
    .q-checkbox__bg, .q-icon {
        color: #6f8ee8;
    }
}
.level-8a2be2 {
    .q-checkbox__bg, .q-icon {
        color: #8a2be2;
    }
}
.level-9f2492 {
    .q-checkbox__bg, .q-icon {
        color: #9f2492;
    }
}
.level-561861 {
    .q-checkbox__bg, .q-icon {
        color: #561861;
    }
}
.level-1c0030 {
    .q-checkbox__bg, .q-icon {
        color: #1c0030;
    }
}

.hovered {
    pointer-events: none;
    stroke: rgb(172, 172, 172);
}

.tooltip-header {
    @media screen and (min-width: $mobile-breakpoint) {
        margin: 1rem 0;
    }
}

.return-periods {
    display: flex;
    flex-direction: column;
}

.sfc-legend {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-left: 4em;

    .sfc-legend-item {
        align-items: center;
        display: flex;
        font-size: 0.9em;
        margin-right: 1em;
    }
}

// tooltip
.streamflow-tooltip {
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 5px;
    color: white;
    display: flex;
    flex-direction: column;
    min-width: 250px;
    padding: 1em;
    pointer-events: none;
    position: fixed;
    z-index: 3;

    &.tooltip {
        background-color: rgba(0, 0, 0);
        position: absolute;
    }
}

.streamflow-tooltip-text {
    display: flex;
    align-items: center;

    .label-legend {
        border: 1px solid white;
        height: 10px;
        width: 10px;
        margin-right: 5px;
    }
}

// map popup functionality
.chart-popup {
    font-family: 'Inter', 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif, 'Inter' !important;
    background-color: white;
    overflow: auto;
    @media screen and (min-width: $mobile-breakpoint) {
        border-radius: 0.3em;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12);
        height: fit-content;
        width: fit-content;
        min-width: 31rem;
        margin-left: -28%;
        max-height: 80vh;
        z-index: 2; // Prevents the line from clipping outside the chart
    }
    @media screen and (max-width: $mobile-breakpoint) {
        position: fixed;
        top: $nav-height;
        left: 0;
        height: calc(100dvh - $nav-height - $mobile-tab-height); // Subtract nav-height and mobile footer controls
        width: 100vw;
        z-index: 101; // Places chart above map elements
        overflow-y: scroll;
        overflow-x: hidden;

        #streamflow-chart-container {
            width: 95vw;
            margin: auto;
            margin-top: 2rem;
        }

        .mobile-close-btn {
            right: 1rem;
            top: 1rem;
            padding: 0.5rem;
        }

        .name-skeleton {
            width: 95vw;
            height: 3rem;
        }

        .chart-skeleton {
            width: 95vw;
        }
    }
}

.past {
    fill: #ECEEF580;
}
</style>
